import React from 'react';
import { Calculator, ExternalLink } from 'lucide-react';
import { StatusBadge } from '../UI/StatusBadge';
import { formatDate } from '../../utils/dateUtils';
import { formatCurrency } from '../../utils/numberUtils';

const resultPresentation = (check) => {
  if (check?.outcome === 'not_required') {
    return {
      status: 'verification_required',
      label: `Scenario ${check.scenario_number} matched`,
      text: 'The calculator indicates that tax clearance may not be required. Verify the scenario conditions and prior Singapore employment before recording the exemption.',
    };
  }
  if (check?.outcome === 'required') {
    return {
      status: 'required',
      label: 'Tax clearance required',
      text: 'The recorded dates and income do not match IRAS Scenario 1, 2, 3 or 4.',
    };
  }
  return {
    status: 'unverified',
    label: 'Check incomplete',
    text: 'Complete the missing employment or annual-income information before relying on this check.',
  };
};

const incomeAnswer = (check) => {
  if (check?.annual_income_at_or_below_threshold === true) return 'Yes';
  if (check?.annual_income_at_or_below_threshold === false) return 'No';
  return 'Not confirmed';
};

const annualIncomeSummary = (check) => (
  (check?.annual_income || [])
    .map((row) => (
      row.known
        ? `${row.year}: ${formatCurrency(row.amount, { grouped: true })}`
        : `${row.year}: not on file`
    ))
    .join(' · ')
);

/**
 * Familiar, read-only rendering of the official IRAS calculator inputs.
 *
 * The backend owns the scenario calculation. This component only presents the
 * versioned result; it never turns a screening result into an exemption.
 */
const IRASCalculatorCheck = ({ check }) => {
  if (!check) return null;

  const result = resultPresentation(check);
  const yearDays = (check.calendar_year_days || [])
    .map((row) => `${row.year}: ${row.days} days`)
    .join(' · ');
  const incomeSummary = annualIncomeSummary(check);

  return (
    <section
      className="max-w-xl rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-4"
      aria-labelledby="iras-calculator-check-title"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-white border border-gray-200">
          <Calculator className="h-4 w-4 text-gray-600" aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <h4 id="iras-calculator-check-title" className="text-sm font-semibold text-gray-900">
            IRAS Tax Clearance Calculator check
          </h4>
          <p className="mt-0.5 text-xs text-gray-500">
            Scenario 1–4 logic, using data already recorded in this portal.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <p className="field-label">Date of commencement</p>
          <p className="field-display">
            {check.commencement_date ? formatDate(check.commencement_date) : 'Not on file'}
          </p>
        </div>
        <div>
          <p className="field-label">Date of cessation</p>
          <p className="field-display">
            {check.cessation_date ? formatDate(check.cessation_date) : 'Not on file'}
          </p>
        </div>
        <div className="sm:col-span-2">
          <p className="field-label">Income for each year is $21,000 or less</p>
          <p className="field-display">{incomeAnswer(check)}</p>
          {incomeSummary && (
            <p className="mt-1 text-xs text-gray-500">
              {incomeSummary} · processed payroll, provisional until final pay is complete
            </p>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-gray-500">Result</span>
          <StatusBadge status={result.status} label={result.label} />
        </div>
        <p className="mt-2 text-sm text-gray-700">{result.text}</p>
        {yearDays && <p className="mt-1 text-xs text-gray-500">{yearDays}</p>}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <p className="text-xs text-gray-500">
          Calculator rules checked {check.rule_version}. This is a pre-check, not an IRAS filing.
        </p>
        <a
          className="btn btn-admin-outline btn-sm w-full sm:w-auto flex-shrink-0"
          href={check.source_url}
          target="_blank"
          rel="noreferrer"
        >
          Open IRAS calculator
          <ExternalLink className="h-3.5 w-3.5 ml-1.5" aria-hidden="true" />
        </a>
      </div>
    </section>
  );
};

export default IRASCalculatorCheck;
